/**
 * Zod schemas and response types for the Resource Plan API (M2).
 *
 * All request bodies are parsed through these schemas at the boundary. Never cast raw
 * `req.json()` — always `.parse()` or `.safeParse()`.
 *
 * Money and hours cross this boundary as STRINGS, never numbers: JSON numbers are
 * float64, and routing a bill rate or a 1,440-cell hours grid through one reintroduces
 * exactly the imprecision src/lib/rate-card.ts and src/lib/resource-plan-math.ts exist to
 * avoid. The regexes are the same shape `parseMinor()` accepts.
 */

import { z } from "zod";
import type { PlanLineStatus } from "@/lib/plan-line-status";
import { RESOURCE_PLAN_STATUSES } from "@/lib/resource-plan-lifecycle";
import type { EstimatedHoursLineView } from "./estimated-hours-lines";
import type { PlanConcernSummary } from "./plan-concerns";

// ── Shared primitives ─────────────────────────────────────────────────────────

/**
 * A `numeric(p,2)` money or hours value, unsigned, as a string.
 *
 * Exported so src/schemas/estimated-hours-lines.ts reuses the exact same shape rather
 * than defining a second, silently-diverging regex for the same `numeric(10,2)` column.
 */
export const decimal2 = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, { message: "must be a number with at most 2 decimal places" });

const percent2 = decimal2.refine((v) => Number(v) <= 100, {
  message: "must be between 0 and 100",
});

/**
 * A month, as `YYYY-MM`. The API accepts the month, not a date, so a caller cannot
 * express "the 15th" at all — `plan_line_month.month` is pinned to the 1st by a CHECK and
 * this is the boundary that makes that impossible to violate rather than merely rejected.
 */
const month = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: "must be a month in YYYY-MM form" });

/** A required, trimmed, bounded name. */
const name = z
  .string()
  .trim()
  .min(1, { message: "a name is required" })
  .max(120, { message: "a name may be at most 120 characters" });

/** An audit reason. RP-6.2 and RP-9.1 require one on specific actions. */
const reason = z
  .string()
  .trim()
  .min(1, { message: "a reason is required" })
  .max(500, { message: "a reason may be at most 500 characters" });

export const PRICING_MODELS = ["TimeAndMaterials", "FixedPrice"] as const;
export const BUDGET_CADENCES = ["Monthly", "Total"] as const;
export const PLAN_KINDS = ["Base", "Option", "ChangeOrder"] as const;
export const PLAN_RATE_MODES = ["Card", "ClientDictated"] as const;
export const PLAN_ENTRY_MODES = ["MonthByMonth", "SteadyState"] as const;
export const CONTRACTED_FEE_BASES = ["Total", "Monthly"] as const;
export const WORKSTREAM_TYPES = [
  "Experience",
  "Workflow",
  "Integration",
  "Foundation",
  "Advisory",
  "RunSupport",
] as const;
export const POD_POSITIONS = ["R1", "R2", "R3", "R4", "Supporting"] as const;

// ── Create a plan (RP-1.1, RP-1.2, RP-1.3) ────────────────────────────────────

/**
 * The New Resource Plan route's extra rule: every plan created from the form names the NOS
 * Deal it is for, and its Solution comes from that Deal. Only the Deal id is read here;
 * the rest of the body is validated by `createResourcePlanSchema`.
 */
export const createPlanFromDealSchema = z
  .object({
    nymblDealId: z
      .string({ message: "Pick the NOS Deal this plan is for" })
      .trim()
      .min(1, { message: "Pick the NOS Deal this plan is for" })
      .max(64),
  })
  .passthrough();

export const createResourcePlanSchema = z
  .object({
    solutionId: z.string().min(1),
    name,
    kind: z.enum(PLAN_KINDS).optional(),
    pricingModel: z.enum(PRICING_MODELS),
    budgetCadence: z.enum(BUDGET_CADENCES),
    startMonth: month,
    endMonth: month.nullable().optional(),
    rateMode: z.enum(PLAN_RATE_MODES).optional(),
    /**
     * Price off the Account's Rate Card (its negotiated rates) ahead of the standard one.
     * Omit for true. False prices the plan on the standard Rate Card even though the
     * Account has negotiated rates. Set at creation only — like the Rate Basis, it is
     * frozen into the plan.
     */
    useAccountRateCard: z.boolean().optional(),
    /**
     * RP-4.4. Omit and it defaults from the cadence: Monthly → SteadyState (a run-rate is
     * one number, not 36 identical ones), Total → MonthByMonth.
     */
    entryMode: z.enum(PLAN_ENTRY_MODES).optional(),
    /** Optional per-plan tier override. NULL inherits the Solution's effective tier. */
    engagementStandardTierId: z.string().min(1).nullable().optional(),
    /**
     * RP-2.1, composed AT creation. Omit for a plan with no Planned Pod yet.
     *
     * The New Resource Plan form has no plan id to POST a compose to, so a Shape picked
     * there can only be applied in the same request that writes the plan — and, because it
     * is applied in the same TRANSACTION, a Shape that turns out to be unknown or retired
     * leaves no half-built plan behind. There is deliberately no `createLines` twin: a
     * brand-new plan has no grid for RP-2.1's lines to be unwelcome in.
     */
    podShapeId: z.string().min(1).optional(),
    estimatedHours: decimal2.nullable().optional(),
    contractedFee: decimal2.nullable().optional(),
    contractedFeeBasis: z.enum(CONTRACTED_FEE_BASES).nullable().optional(),
    contractedFeeReference: z.string().trim().max(120).nullable().optional(),
    contractedFeeReceivedAt: z.iso
      .datetime({ message: "contractedFeeReceivedAt must be an ISO 8601 datetime" })
      .nullable()
      .optional(),
    contingencyPercent: percent2.nullable().optional(),
    discountPercent: percent2.nullable().optional(),
    discountAmount: decimal2.nullable().optional(),
    discountReason: reason.nullable().optional(),
    commercialNotes: z.string().trim().max(4000).nullable().optional(),
    /**
     * The NOS Gateway Deal this plan is for. The server verifies it and snapshots NOS's id
     * and name (src/api/plan-deal-options.ts). Optional at this layer — internal callers
     * create plans without one — but the New Resource Plan route requires it and derives
     * `solutionId` from the Deal (`createPlanFromDealSchema`, `createResourcePlanFromDeal`).
     */
    nymblDealId: z.string().trim().min(1).max(64).nullable().optional(),
  })
  // The same rules the CHECK constraints enforce, restated here so the caller gets a
  // readable message instead of a Postgres constraint name.
  .refine((v) => v.budgetCadence !== "Total" || (v.endMonth ?? null) !== null, {
    message: "a Total-cadence plan needs an end month",
    path: ["endMonth"],
  })
  // The fee is forbidden only on T&M × Monthly — a pure run-rate retainer has no ceiling
  // to compare against (§4.1). On T&M × Total the fee IS the not-to-exceed envelope; see
  // the note on resource_plan_fee_not_on_tm_monthly in src/db/schema/resource-plans.ts.
  .refine(
    (v) =>
      (v.contractedFee ?? null) === null ||
      !(v.pricingModel === "TimeAndMaterials" && v.budgetCadence === "Monthly"),
    {
      message:
        "a Monthly T&M plan is a run-rate with no fee — use a Total cadence for a not-to-exceed ceiling",
      path: ["contractedFee"],
    },
  )
  .refine(
    (v) => (v.contractedFeeBasis ?? null) === null || v.contractedFeeBasis === v.budgetCadence,
    {
      message: "the Contracted Fee basis must match the plan's Budget Cadence",
      path: ["contractedFeeBasis"],
    },
  )
  .refine(
    (v) => ((v.contractedFee ?? null) === null) === ((v.contractedFeeBasis ?? null) === null),
    {
      message: "a Contracted Fee and its basis must be set together",
      path: ["contractedFeeBasis"],
    },
  )
  .refine((v) => (v.discountPercent ?? null) === null || (v.discountAmount ?? null) === null, {
    message: "set a discount as a percentage or an amount, not both",
    path: ["discountAmount"],
  })
  .refine(
    (v) =>
      ((v.discountPercent ?? null) === null && (v.discountAmount ?? null) === null) ||
      (v.discountReason ?? null) !== null,
    { message: "a discount needs a reason", path: ["discountReason"] },
  )
  .refine((v) => (v.endMonth ?? null) === null || v.endMonth! >= v.startMonth, {
    message: "the end month cannot precede the start month",
    path: ["endMonth"],
  });

/**
 * A plan header edit. Every field optional; the same cross-field rules are re-checked
 * server-side against the MERGED row, because a partial body cannot see them alone.
 */
export const updateResourcePlanSchema = z.object({
  name: name.optional(),
  kind: z.enum(PLAN_KINDS).optional(),
  pricingModel: z.enum(PRICING_MODELS).optional(),
  budgetCadence: z.enum(BUDGET_CADENCES).optional(),
  startMonth: month.optional(),
  endMonth: month.nullable().optional(),
  rateMode: z.enum(PLAN_RATE_MODES).optional(),
  /**
   * Switching to MonthByMonth MATERIALISES each line's run-rate into every month, so
   * nothing is lost (RP-4.4). Switching to SteadyState is only legal on a Monthly plan.
   */
  entryMode: z.enum(PLAN_ENTRY_MODES).optional(),
  engagementStandardTierId: z.string().min(1).nullable().optional(),
  /*
   * `estimatedHours` is intentionally ABSENT here. Once a plan exists, its estimate is a
   * server-maintained rollup of its Estimated Hours Lines (src/api/estimated-hours-lines.ts) —
   * write a line (or restructure the tree), not this column. It stays on the CREATE schema
   * above only to seed a first root line at creation time; see createResourcePlan.
   */
  contractedFee: decimal2.nullable().optional(),
  contractedFeeBasis: z.enum(CONTRACTED_FEE_BASES).nullable().optional(),
  contractedFeeReference: z.string().trim().max(120).nullable().optional(),
  contractedFeeReceivedAt: z.iso.datetime().nullable().optional(),
  contingencyPercent: percent2.nullable().optional(),
  discountPercent: percent2.nullable().optional(),
  discountAmount: decimal2.nullable().optional(),
  discountReason: reason.nullable().optional(),
  commercialNotes: z.string().trim().max(4000).nullable().optional(),
});

/**
 * RP-9.1. `reason` is required for Lost and carried into `audit_log.reason`.
 *
 * `to` accepts EVERY status, not just the four the transition map has edges for. A target
 * the map cannot reach by an edge is handled by the direct-set path in
 * src/api/plan-lifecycle.ts — see `REQUIRED_FIELDS_BY_STATUS` in
 * src/lib/resource-plan-lifecycle.ts for what survives that route. Narrowing the enum here
 * would make the boundary, rather than the lifecycle module, the thing deciding which
 * statuses exist, and the two would drift the first time M5 adds an edge.
 */
/**
 * An [RM] or [ADMIN] rejecting a plan back to its author: sets the Resourcing status to
 * Returned. The reason is REQUIRED — it is what the author acts on, and it is shown to
 * them on the plan. The plan's Draft / Proposed / Won status is not touched.
 */
export const rejectPlanSchema = z.object({
  reason: z.string().trim().min(1, "a rejection needs a reason the author can act on").max(1000),
});

/** Why a plan was sent back, as the author reads it on the plan. */
export interface PlanRejectionView {
  reason: string;
  byName: string | null;
  at: string;
}

/** One row of the Rejected Plans report — one per rejection, so a plan can appear twice. */
export interface PlanRejectionReportRow {
  id: string;
  /** Null once the plan itself has been deleted; `planName` is the snapshot. */
  resourcePlanId: string | null;
  planName: string;
  solutionName: string | null;
  clientAccountName: string | null;
  /** The plan's lifecycle status now (it is left alone by a rejection). */
  planStatus: string | null;
  rejectedAt: string;
  reason: string;
  rejectedByName: string | null;
  /** The plan's owner when it was rejected. */
  creatorName: string | null;
  /** Who sent the plan out; null when the audit trail cannot say. */
  submitterName: string | null;
}

export const planTransitionSchema = z.object({
  to: z.enum(RESOURCE_PLAN_STATUSES),
  wonDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  sowReference: z.string().trim().max(120).nullable().optional(),
  lostReason: reason.nullable().optional(),
  /**
   * RP-17.1's "the planner's note", carried on the Propose request (M4).
   *
   * It rides on the transition rather than on a second "request approval" call because the
   * planner does not know whether approval is needed — the gate decides that, from cost
   * they cannot see. Asking them to press Propose, be told approval is required, and then
   * press a different button to say why would make the note an afterthought.
   */
  approvalNote: z.string().trim().max(1000).nullable().optional(),
  /**
   * Why a status was SET rather than transitioned (the override path only).
   *
   * Optional by decision: the guardrail chosen for this feature is "same as authoring, no
   * extra ceremony", so an override is not blocked on a justification. When it is given it
   * lands in `audit_log.reason` and shows in Plan History.
   */
  statusChangeReason: z.string().trim().max(1000).nullable().optional(),
});

// ── Workstreams (RP-3.1) ──────────────────────────────────────────────────────

export const createWorkstreamSchema = z.object({
  name,
  type: z.enum(WORKSTREAM_TYPES),
  description: z.string().trim().max(2000).nullable().optional(),
  estimatedHours: decimal2.nullable().optional(),
  startMonth: month.nullable().optional(),
  endMonth: month.nullable().optional(),
});

/*
 * `.omit({ estimatedHours: true })`, not a bare `.partial()`: once a Workstream exists its
 * estimate is a server-maintained rollup of its Estimated Hours Lines
 * (src/api/estimated-hours-lines.ts), so the field is legal on CREATE (to seed a first root
 * line) but must not be independently patchable afterwards.
 */
export const updateWorkstreamSchema = createWorkstreamSchema.partial().omit({
  estimatedHours: true,
});

/** RP-3.1: "Workstreams reorder". An explicit order, so a drag never has to guess. */
export const reorderWorkstreamsSchema = z.object({
  workstreamIds: z.array(z.string().min(1)).min(1, { message: "nothing to reorder" }),
});

// ── Plan Lines (RP-4.2, RP-6.2, RP-6.3) ───────────────────────────────────────

export const createPlanLineSchema = z.object({
  workstreamId: z.string().min(1).nullable().optional(),
  laborCategoryId: z.string().min(1),
  clientLaborCategory: z.string().trim().max(120).nullable().optional(),
  position: z.enum(POD_POSITIONS).nullable().optional(),
  /**
   * Omit to take the resolved rate for the plan's basis (RP-6.1). Supplying one is an
   * override and needs `rateOverrideReason` — except in Client-Dictated mode, where the
   * rate IS the input and the contract is its own reason (RP-6.3).
   */
  billRate: decimal2.optional(),
  rateOverrideReason: reason.nullable().optional(),
  /**
   * RP-4.4. On a SteadyState plan the inline add row takes the run-rate straight away, so
   * adding a line to a retainer is one action rather than "add, then go and type a number".
   * Ignored on a MonthByMonth plan, where hours are typed into the grid.
   */
  steadyStateHours: decimal2.nullable().optional(),
});

export const updatePlanLineSchema = z.object({
  workstreamId: z.string().min(1).nullable().optional(),
  laborCategoryId: z.string().min(1).optional(),
  clientLaborCategory: z.string().trim().max(120).nullable().optional(),
  position: z.enum(POD_POSITIONS).nullable().optional(),
  billRate: decimal2.optional(),
  rateOverrideReason: reason.nullable().optional(),
  /** RP-4.4. The run-rate, on a SteadyState plan. */
  steadyStateHours: decimal2.nullable().optional(),
  /** Pass null to clear the override and fall back to the card rate. */
  resetRateToCard: z.boolean().optional(),
});

export const reorderPlanLinesSchema = z.object({
  planLineIds: z.array(z.string().min(1)).min(1, { message: "nothing to reorder" }),
});

/** RP-32.1 — why a Suggested Resource was named. Mirrors the `suggested_resource_reason` pgEnum. */
export const SUGGESTED_RESOURCE_REASONS = [
  "ClientRequested",
  "PitchedToClient",
  "Continuity",
  "Other",
] as const;
export type SuggestedResourceReason = (typeof SUGGESTED_RESOURCE_REASONS)[number];

/**
 * RP-18.2. `resourceId: null` clears the suggestion.
 *
 * RP-32.1: `reason` / `reasonNote` — omitted means "keep the current reason" when the
 * Resource is unchanged; naming a different Resource always starts from no reason unless
 * one is sent with it. `null` clears.
 */
export const setSuggestedResourceSchema = z
  .object({
    resourceId: z.string().min(1).nullable(),
    confirmed: z.boolean().optional(),
    reason: z.enum(SUGGESTED_RESOURCE_REASONS).nullable().optional(),
    reasonNote: z
      .string()
      .trim()
      .max(200, { message: "Keep the note to 200 characters." })
      .nullable()
      .optional(),
  })
  .superRefine((value, ctx) => {
    const note =
      value.reasonNote === undefined || value.reasonNote === "" ? null : value.reasonNote;
    if (value.reason === "Other" && note === null) {
      ctx.addIssue({
        code: "custom",
        path: ["reasonNote"],
        message: "Say why when the reason is Other.",
      });
    }
    if (value.resourceId === null && value.reason !== undefined && value.reason !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["reason"],
        message: "A reason needs a Suggested Resource.",
      });
    }
  });

/**
 * RP-24 Line Shares — the WHOLE Share set of one Plan Line, replaced in one call.
 *
 * `shares: []` un-splits the line. One Share collapses back to that Resource as the line's
 * Confirmed Suggested Resource. Two or more split it. MonthByMonth plans send `months`;
 * SteadyState plans send `steadyStateHours` — the wrong shape for the plan is refused.
 * The cap (a Planning Constant, default 3) is enforced server-side, not here.
 */
export const setPlanLineSharesSchema = z.object({
  shares: z
    .array(
      z.object({
        resourceId: z.string().min(1),
        // RM-entered, never derived from hours (resource-plan.md §2 #1).
        points: z.number().int().min(0).max(40),
        months: z
          .array(z.object({ month, hours: decimal2 }))
          .max(120)
          .optional(),
        steadyStateHours: decimal2.nullable().optional(),
      }),
    )
    .max(10, { message: "too many Shares" }),
});

// ── Resourcing Status + Owner (Resource Plans list) ──────────────────────────

/**
 * A Resource Manager's own staffing flag on a plan, independent of the lifecycle
 * `status`. Mirrors the `plan_resourcing_status` pgEnum; null on the wire means "Not set".
 */
export const PLAN_RESOURCING_STATUSES = ["InReview", "Staffing", "Staffed", "Returned"] as const;
export type PlanResourcingStatus = (typeof PLAN_RESOURCING_STATUSES)[number];

/** What the RM can pick from the control. Returned is reached only through Reject, which
 * carries the required reason. */
export const SETTABLE_PLAN_RESOURCING_STATUSES = ["InReview", "Staffing", "Staffed"] as const;

/** `resourcingStatus: null` clears the flag back to "Not set". */
export const setPlanResourcingStatusSchema = z.object({
  resourcingStatus: z.enum(SETTABLE_PLAN_RESOURCING_STATUSES).nullable(),
});

/** Reassign a plan's Owner (shown as "Author"). */
export const setPlanOwnerSchema = z.object({
  ownerUserId: z.string().trim().min(1, { message: "ownerUserId is required" }),
});

// ── The hours grid (RP-4.1) ───────────────────────────────────────────────────

/**
 * One batched save for the whole grid.
 *
 * A cell per keystroke would be 1,440 requests; the grid keeps a local draft and sends
 * only the dirty cells. `hours: null` DELETES the cell rather than storing zero, which is
 * how a 27-month plan avoids carrying 1,440 rows of nothing — see the note in
 * src/db/schema/plan-line-months.ts.
 */
/**
 * The hard cap on cells in one `saveGrid` batch. Exported so any OTHER caller that
 * assembles a `cells` array itself (e.g. `solution-estimate-import.ts`) can pre-check
 * against it before opening a write transaction, rather than discovering the limit only
 * when `saveGrid` throws — see that module's SteadyState/cap pre-flight.
 */
export const MAX_GRID_CELLS_PER_SAVE = 5_000;

export const saveGridSchema = z.object({
  /**
   * RP-4.4 — the SteadyState payload: one hours-per-month figure per Plan Line, which the
   * server expands across the plan's months. `hours: null` clears the run-rate.
   *
   * Sent instead of `cells` when the plan is in SteadyState mode. A plan in MonthByMonth
   * rejects these and vice versa, so a stale client cannot write the wrong shape.
   */
  runRates: z
    .array(z.object({ planLineId: z.string().min(1), hours: decimal2.nullable() }))
    .max(200, { message: "too many Plan Lines in one save" })
    .optional(),
  cells: z
    .array(
      z.object({
        planLineId: z.string().min(1),
        month,
        hours: decimal2.nullable(),
      }),
    )
    // The 40 × 36 bar is 1,440 cells; the cap is generous enough for a full-grid paste
    // and tight enough that a runaway client cannot ask the server to write forever.
    .max(MAX_GRID_CELLS_PER_SAVE, { message: "too many cells in one save" })
    .optional(),
});

// ── M3: Workstream Templates (Epic 13, RP-13.1) ───────────────────────────────

export const WORKSTREAM_SIZE_PRESETS = ["S", "M", "L", "XL"] as const;

/**
 * Save a Workstream Template.
 *
 * Every save creates a NEW VERSION and supersedes the current one — RP-13.1: "Templates
 * are versioned. Changing a template never alters existing Workstreams." There is no
 * partial update, because a mix is only meaningful in full: patching one line's percentage
 * would leave the template not summing to 100%.
 *
 * "Sums to exactly 100.00" is checked here rather than only in the API, so the boundary
 * refuses an unbalanced mix before any DB work starts. Summed as integer hundredths —
 * `Number()` on "33.33" three times does not make 100.
 */
export const saveWorkstreamTemplateSchema = z
  .object({
    description: z.string().trim().max(2000).nullable().optional(),
    sizeSHours: decimal2,
    sizeMHours: decimal2,
    sizeLHours: decimal2,
    sizeXlHours: decimal2,
    lines: z
      .array(
        z.object({
          laborCategoryId: z.string().min(1),
          mixPercent: percent2,
        }),
      )
      .min(1, { message: "a template needs at least one Labor Category" })
      .max(50),
  })
  .refine(
    (v) => v.lines.reduce((sum, l) => sum + Math.round(Number(l.mixPercent) * 100), 0) === 10_000,
    { message: "the mix must sum to exactly 100%" },
  )
  .refine((v) => new Set(v.lines.map((l) => l.laborCategoryId)).size === v.lines.length, {
    message: "each Labor Category may appear only once in a template",
  })
  .refine(
    (v) =>
      Number(v.sizeSHours) <= Number(v.sizeMHours) &&
      Number(v.sizeMHours) <= Number(v.sizeLHours) &&
      Number(v.sizeLHours) <= Number(v.sizeXlHours),
    { message: "size presets must be ordered S ≤ M ≤ L ≤ XL" },
  );

// ── M3: Size a Workstream (Epic 3, RP-3.2) ────────────────────────────────────

/**
 * RP-3.2: "Input accepts Total Hours, or a Size preset (S/M/L/XL) mapping to default
 * hours per Workstream Type."
 *
 * Exactly one of the two, never both: a request carrying 800 hours AND "M" is ambiguous
 * about which the planner meant, and picking one silently is how a plan ends up a
 * different size than the person who sized it believes.
 */
export const sizeWorkstreamSchema = z
  .object({
    totalHours: decimal2.optional(),
    sizePreset: z.enum(WORKSTREAM_SIZE_PRESETS).optional(),
    /**
     * Whether to clear the Workstream's existing Plan Lines first.
     *
     * Defaults to false — additive re-sizing keeps hand-edited lines the template does
     * not know about, which is the behaviour a planner re-sizing an in-progress
     * Workstream expects. `true` is the "start again from the template" action, and the
     * UI confirms it because it destroys typed cells.
     */
    replaceExisting: z.boolean().optional(),
  })
  .refine((v) => (v.totalHours === undefined) !== (v.sizePreset === undefined), {
    message: "supply either total hours or a size preset, not both",
  });

// ── M3: Override a Workstream's mix (Epic 3, RP-3.3) ──────────────────────────

/**
 * RP-3.3. `lines: null` CLEARS the override and returns the Workstream to its template's
 * mix; a list replaces it wholesale. There is no partial form, for the same reason
 * `saveWorkstreamTemplateSchema` has none.
 */
export const setWorkstreamMixSchema = z
  .object({
    lines: z
      .array(
        z.object({
          laborCategoryId: z.string().min(1),
          mixPercent: percent2,
        }),
      )
      .min(1)
      .max(50)
      .nullable(),
    /** RP-3.3: "Override requires a short reason." Ignored when clearing. */
    reason: reason.optional(),
  })
  .refine(
    (v) =>
      v.lines === null ||
      v.lines.reduce((sum, l) => sum + Math.round(Number(l.mixPercent) * 100), 0) === 10_000,
    { message: "the mix must sum to exactly 100%" },
  )
  .refine((v) => v.lines === null || v.reason !== undefined, {
    message: "overriding the mix needs a reason",
  })
  .refine(
    (v) =>
      v.lines === null || new Set(v.lines.map((l) => l.laborCategoryId)).size === v.lines.length,
    { message: "each Labor Category may appear only once in a mix" },
  );

// ── M3: Ramp factors (RP-4.3) ─────────────────────────────────────────────────

/**
 * RP-4.3: "Ramp factor row above the grid, default 1.0, range 0–1.0."
 *
 * A sparse patch: only the months named are changed. A factor of exactly 1.000 DELETES
 * the row, because absence is the default — see the note on `plan_month_ramp`.
 */
export const setPlanRampSchema = z.object({
  months: z
    .array(
      z.object({
        month,
        factor: z
          .string()
          .regex(/^\d(\.\d{1,3})?$/, {
            message: "must be a number from 0 to 1 with at most 3 decimal places",
          })
          .refine((v) => Number(v) <= 1, { message: "a ramp factor may not exceed 1.0" }),
      }),
    )
    .max(600),
});

// ── M3: Planned Pod (Epic 2) ──────────────────────────────────────────────────

/** Positions are R1–R4. `Supporting` is a Plan Line label, never a Position (RP-2.1). */
export const PLANNED_POD_POSITIONS = ["R1", "R2", "R3", "R4"] as const;

/**
 * RP-2.1 / RP-2.3. Either start from a Pod Shape, or declare the Pod Custom.
 *
 * `createLines` (default true) is RP-2.1's "Selecting a Shape creates one plan-level Plan
 * Line per Position". It is a flag because the same endpoint composes a Pod on a plan
 * that already has a full grid, where creating five more lines would be unwelcome.
 */
export const composePlannedPodSchema = z
  .object({
    shapeId: z.string().min(1).nullable(),
    createLines: z.boolean().optional(),
  })
  .refine((v) => v.shapeId !== undefined, { message: "a Pod Shape id, or null for Custom" });

/** RP-2.2 / RP-2.3: add a Position beyond the Shape, or to a Custom Pod. */
export const createPlannedPodPositionSchema = z.object({
  positionCode: z.enum(PLANNED_POD_POSITIONS),
  expectedDisciplineId: z.string().min(1),
  /** Nullable: a Discipline with no priced Labor Category carries none (the CS gap). */
  laborCategoryId: z.string().min(1).nullable().optional(),
});

/** RP-2.2: re-discipline a Position, or change the Labor Category behind it. */
export const updatePlannedPodPositionSchema = z.object({
  expectedDisciplineId: z.string().min(1).optional(),
  laborCategoryId: z.string().min(1).nullable().optional(),
});

/** RP-2.4: link a Plan Line to a Planned Pod Position, or unlink it. */
export const setPlanLinePositionSchema = z.object({
  plannedPodPositionId: z.string().min(1).nullable(),
});

// ── M3: Solve (Epic 5) ────────────────────────────────────────────────────────

/**
 * RP-5.1 / RP-5.2 / RP-5.3, as one discriminated union so an impossible request —
 * a budget AND a margin target — cannot be expressed.
 *
 * The same body is posted to both `/solve` (which computes and returns a proposal) and
 * `/solve/apply` (which recomputes and writes). Apply deliberately re-derives from these
 * inputs rather than accepting a list of per-line hours: a client-supplied hours array
 * would let a caller write any hours it liked under the guise of "applying a solve",
 * bypassing nothing in particular today but making the endpoint a general-purpose grid
 * write with no cell validation.
 */
export const planSolveSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("budget"), targetRevenue: decimal2 }),
  z.object({ kind: z.literal("hours"), targetHours: decimal2 }),
  z.object({ kind: z.literal("targetMargin"), targetMarginPercent: percent2 }),
]);

/** RP-5.1: "Locked Plan Lines hold constant." */
export const setPlanLineLockSchema = z.object({ locked: z.boolean() });

// ── Response types ────────────────────────────────────────────────────────────
//
// Interfaces, not Zod schemas — the response shape is not parsed, it is constructed. Cost
// keys are declared OPTIONAL so that omitting them for [ACCT] type-checks and including
// them is a deliberate choice. See shapePlanForActor() in src/api/resource-plans.ts.

export interface PlanLineMonthView {
  month: string;
  hours: string;
}

/**
 * RP-24 — one Resource's Share of a split Plan Line. `points` is RM-entered and present ONLY
 * for a reader holding `managePlanLineShares` (ADMIN / RM); every other reader sees the name
 * and the hours (RP-24.4).
 */
export interface PlanLineShareView {
  resourceId: string;
  resourceName: string;
  /** Entered hours, `YYYY-MM`. In SteadyState, the derived expansion of `steadyStateHours`. */
  months: PlanLineMonthView[];
  steadyStateHours: string | null;
  /** Entered hours across the plan. */
  totalHours: string;
  points?: number;
}

/** RP-24 — a split line's per-month balance of Shares against the line (entered hours). */
export interface PlanLineShareBalanceView {
  unallocatedHours: string;
  overHours: string;
  months: Array<{ month: string; unallocatedHours: string; overHours: string }>;
}

export interface PlanLineView {
  id: string;
  workstreamId: string | null;
  /** RP-2.4: the Planned Pod Position this line covers, or null when unlinked. */
  plannedPodPositionId?: string | null;
  /** RP-5.1: held constant by the solvers. Never affects the grid or a save. */
  locked?: boolean;
  laborCategoryId: string;
  laborCategoryName: string;
  /** Present when the Labor Category maps to a Discipline (RP-11.2). */
  disciplineCode: string | null;
  clientLaborCategory: string | null;
  position: string | null;
  billRate: string;
  rateSource: string;
  rateOverrideReason: string | null;
  /** The card rate for this category on the plan's basis, when there is one (RP-6.2). */
  listRate: string | null;
  suggestedResourceId: string | null;
  suggestedResourceName: string | null;
  suggestedResourceConfirmed: boolean;
  /**
   * RP-32.1. Optional on the type only because frozen snapshots written before it existed
   * lack it; every live read sets it (null when none).
   */
  suggestedResourceReason?: SuggestedResourceReason | null;
  suggestedResourceReasonNote?: string | null;
  /**
   * RP-32.2 — how many active Candidates the line has. A count only, never who: the
   * Candidates themselves are fetched separately, so they never enter a frozen version,
   * the client export or the shared overview. Absent on snapshots.
   */
  activeCandidateCount?: number;
  /** RP-32.4 — non-deleted notes on the line (a count only; bodies are fetched separately). */
  noteCount?: number;
  sortOrder: string;
  months: PlanLineMonthView[];
  /**
   * The run-rate — hours per month — when the plan is in SteadyState mode (RP-4.4).
   * `months` above is then the DERIVED expansion, present so every consumer sees the
   * same shape in both modes.
   */
  steadyStateHours: string | null;

  // Derived, per line.
  totalHours: string;
  totalRevenue: string;
  /** Indicative only — a Contracted Fee has no per-line allocation (RP-7.1). */
  marginPercent: string | null;
  /** RP-6.2: a boolean, never the magnitude. Reaches [ACCT] safely. */
  belowCost: boolean;
  /**
   * Where the line's cost came from (2026-10-14): its Labor Category, its Suggested
   * Resource's Internal Rate, or both across months. Optional so a version frozen before
   * this existed still reads — absent means `labor_category`. When it is not
   * `labor_category` and the reader lacks `viewResourceInternalRates`, `marginPercent` is
   * null and the three cost keys below are absent: that would be a named Resource's cost.
   */
  internalRateSource?: "labor_category" | "resource" | "mixed";

  /**
   * Absent without `viewPlanCost` (non-negotiable 3), and on a Resource-priced line
   * without `viewResourceInternalRates`. Hour-weighted when a Resource's rate changes
   * mid-plan.
   */
  costRate?: string;
  /** Absent without `viewPlanCost`, or on a Resource-priced line as above. */
  totalCost?: string;
  /** Absent without `viewPlanCost`, or on a Resource-priced line as above. */
  totalNet?: string;

  /**
   * RP-24 Line Shares. Empty on an unsplit line; two or three entries on a split one, whose
   * `suggestedResourceId` is then null. Absent on a frozen version — Shares are live
   * staffing state, never part of what was sold.
   */
  shares?: PlanLineShareView[];
  /** Present only on a split line. Amber when `unallocatedHours` > 0, red when `overHours` > 0. */
  shareBalance?: PlanLineShareBalanceView;
}

export interface WorkstreamView {
  /** null for the plan-level group — lines outside any Workstream (RP-3.1). */
  id: string | null;
  name: string;
  type: string | null;
  description: string | null;
  estimatedHours: string | null;
  startMonth: string | null;
  endMonth: string | null;
  sortOrder: string;

  /** EFFECTIVE hours — entered × ramp (RP-4.3). What the Workstream will actually bill. */
  totalHours: string;
  /**
   * What was typed into this Workstream's cells, before ramp. Equal to `totalHours` on an
   * unramped plan.
   *
   * Both are needed because they answer different questions, and RP-3.2's template drift
   * is measured against the ENTERED figure: a ramp de-rates a month, it does not change
   * what the planner distributed. A sizing dialog that showed the effective figure under
   * an "entered" label would contradict its own 0% drift chip.
   */
  enteredHours?: string;
  totalRevenue: string;
  blendedRate: string | null;
  marginPercent: string | null;
  lineCount: number;

  /** Estimate reconciliation, at Workstream grain (the Estimates panel). */
  estimateVariancePercent: string | null;
  estimateVarianceHours: string | null;
  estimateBand: string;

  // ── M3 Level 1 sizing (RP-3.2 / RP-3.3) ──
  /** The Workstream Template VERSION this was sized from, if any. */
  workstreamTemplateId?: string | null;
  workstreamTemplateVersion?: number | null;
  /** Which preset was clicked, if one was. */
  sizePreset?: string | null;
  /**
   * RP-3.2's "Template Hours vs Entered Hours": what was distributed, frozen at
   * distribution time. `totalHours` above is what has since been typed.
   */
  templateHours?: string | null;
  /** templateHours − totalHours as a signed percentage of templateHours. Null with no template. */
  templateDriftPercent?: string | null;
  /** RP-3.3: set when this Workstream's mix overrides its template's. */
  mixOverrideReason?: string | null;
  /** The effective mix — the override where one exists, else the template's. */
  mix?: WorkstreamMixLineView[];

  totalCost?: string;
  totalNet?: string;
}

/** One Labor Category's share of a Workstream's effective mix (RP-3.3). */
export interface WorkstreamMixLineView {
  laborCategoryId: string;
  laborCategoryName: string;
  /** The percentage in force — the override where one exists, else the template's. */
  mixPercent: string;
  /** The template's percentage, for RP-3.3's "template % vs current %" side-by-side. */
  templateMixPercent: string | null;
}

export interface PlanMonthView {
  month: string;
  /** EFFECTIVE hours — entered × ramp (RP-4.3). Drives revenue, cost and FTE. */
  totalHours: string;
  /** What was typed into this column, before ramp. Equal to `totalHours` when unramped. */
  enteredHours?: string;
  /** RP-4.3's factor, `numeric(4,3)`. "1.000" where none is set. */
  rampFactor?: string;
  totalRevenue: string;
  /** hours ÷ hoursPerFteMonth. A reference figure, never persisted (§4.5). */
  fte: string;
  /**
   * What the month earns on the margin of record (RP-4.1 / RP-D16): `totalRevenue` on T&M,
   * the fee on a Monthly-basis fee, the Fee Spread on Fixed Price × Total. Null on a Fixed
   * Price × Total plan with no hours. See src/lib/fee-spread.ts. Safe for [ACCT] — the fee
   * and the hours it is spread on are both already theirs.
   */
  revenueOfRecord?: string | null;
  /** Margin on `revenueOfRecord`. A percentage, so [ACCT]-safe (non-negotiable 3, RP-D13). */
  marginPercent?: string | null;
  totalCost?: string;
  /** `revenueOfRecord` − `totalCost`. Cost-derived: present only with `viewPlanCost`. */
  totalNet?: string;
}

export interface ResourcePlanSummaryView {
  id: string;
  solutionId: string;
  solutionName: string;
  clientAccountId: string;
  clientAccountName: string;
  /** CRM-owned, read-only — synced from the account's AccountPerson role assignment. */
  crmCsmName: string | null;
  crmClientPartnerName: string | null;
  name: string;
  kind: string;
  status: string;
  pricingModel: string;
  budgetCadence: string;
  startMonth: string;
  endMonth: string | null;
  rateMode: string;
  /** MonthByMonth | SteadyState (RP-4.4). */
  entryMode: string;
  rateBasisId: string;
  rateBasisLabel: string;
  ownerUserId: string | null;
  ownerName: string | null;
  updatedAt: string;

  totalHours: string;
  totalRevenue: string;
  blendedRate: string | null;
  marginOfRecordPercent: string | null;
  marginOfRecordKind: string;
  /** green | amber | red, or null on a plan with no revenue (RP-7.1). */
  marginStatus: string | null;
  lineCount: number;
  /**
   * Which version the plan is at (RP-1.1, RP-15.1's "version" column).
   *
   * M4 addition, optional on the type so an older cached payload still parses. Frozen
   * snapshots are their own resource — this is the pointer, not the history.
   */
  versionNumber?: number;
  /**
   * The Resource Manager's staffing flag; null is "Not set".
   *
   * OPTIONAL because a frozen version's document and an older cached payload have no key.
   */
  resourcingStatus?: PlanResourcingStatus | null;
  /**
   * While the plan is Locked, the status it was locked from; null otherwise. OPTIONAL for
   * the same reason as `resourcingStatus`. Read through `effectivePlanStatus`.
   */
  lockedFromStatus?: string | null;
  /**
   * The plan's Solution's Constraints (Domain / Platform / Offering / ICP), sorted by
   * group then name and deduplicated — what the list's Domain / ICP / Platform filters
   * read. OPTIONAL for the same reason as `resourcingStatus`.
   */
  solutionConstraints?: PlanConstraintTagView[];
  /**
   * Set while the plan's Resourcing status is Returned; cleared when the RM moves it off
   * Returned. OPTIONAL for the same reason as `resourcingStatus`.
   */
  rejection?: PlanRejectionView | null;
  /**
   * RP-34 — unresolved Staffing Concerns on the plan, for badges. Optional: absent on a
   * frozen version and on older payloads, which read as "none".
   */
  concerns?: PlanConcernSummary;
  /**
   * Live mirror of how urgent the Solution's open Staffing Requests are (RP-18.4): the most
   * urgent priority among them and how many are open. Null when none are open. Read-only
   * and never stored on the plan. OPTIONAL for the same reason as `resourcingStatus`.
   */
  staffingNeed?: PlanStaffingNeedView | null;
  /**
   * Distinct Suggested Resource names across the plan's lines, sorted — what the list's
   * search matches so "which plans is this Resource on?" has an answer. OPTIONAL for the
   * same reason as `resourcingStatus`.
   */
  suggestedResourceNames?: string[];
  /** Distinct Labor Category names across the plan's lines, sorted. Searched too. */
  laborCategoryNames?: string[];
  /**
   * Resources currently staffed on the plan's Solution (an Assignment with no end date),
   * sorted. Searched too, so a Resource is found on plans they are already working on, not
   * only ones they are suggested for. OPTIONAL for the same reason as `resourcingStatus`.
   */
  staffedResourceNames?: string[];
}

/** See `ResourcePlanSummaryView.staffingNeed`. */
export interface PlanStaffingNeedView {
  /**
   * `rank` is the priority's position among the ACTIVE priorities (0 = the most urgent
   * level), so the badge can colour by position rather than by the admin-editable name or
   * sort number.
   */
  priority: { name: string; sortOrder: number; rank: number } | null;
  openRequestCount: number;
}

/** One Constraint on a plan's Solution, by its group's name and its own. */
export interface PlanConstraintTagView {
  group: string;
  name: string;
}

/** A user a plan's Owner can be reassigned to — active (not banned) users only. */
export interface PlanOwnerOptionView {
  id: string;
  name: string;
  /** Only sent when another active user shares this name — null otherwise. */
  email: string | null;
}

/**
 * A plan's NOS Demand link (src/api/plan-nos-demand.ts). Plan months in `lockedMonths`
 * are closed in NOS: they hold NOS's hours and the grid shows them read-only.
 */
export interface PlanNosDemandLinkView {
  demandId: string;
  demandName: string | null;
  /** `YYYY-MM`, or null when none of the plan's months were closed in NOS. */
  closedThrough: string | null;
  /** `YYYY-MM`, oldest first. */
  lockedMonths: string[];
  linkedAt: string | null;
  /** The last successful Post to NOS, or null. */
  postedAt: string | null;
  /**
   * The plan has open-month hours NOS hasn't been given since the last link, refresh or
   * post. On the live plan only — a frozen version leaves it out.
   */
  editsToPost?: boolean;
}

export interface ResourcePlanDetailView extends ResourcePlanSummaryView {
  /**
   * Whether this plan prices off its Account's Rate Card ahead of the standard one —
   * chosen at creation. OPTIONAL because a frozen version written before this field
   * existed has no key; absent reads as true, which is how those plans resolved.
   */
  useAccountRateCard?: boolean;
  /**
   * The Solution this plan prices, for the header's context bar.
   *
   * OPTIONAL because an older cached payload has no key at all — the header renders
   * nothing rather than `undefined` when it is absent.
   */
  solutionContext?: SolutionContextView;
  engagementStandardTierId: string | null;
  engagementStandardTierName: string | null;
  estimatedHours: string | null;
  contractedFee: string | null;
  contractedFeeBasis: string | null;
  contractedFeeReference: string | null;
  contractedFeeReceivedAt: string | null;
  contingencyPercent: string | null;
  discountPercent: string | null;
  discountAmount: string | null;
  discountReason: string | null;
  commercialNotes: string | null;
  /** The NOS Gateway Deal this plan was seeded from at creation, if any (a snapshot). */
  nymblDealId: string | null;
  nymblDealName: string | null;
  /**
   * The one NOS Demand this plan's guidance belongs to, or null when not linked. Absent
   * on a version frozen before the link existed.
   */
  nosDemand?: PlanNosDemandLinkView | null;
  wonDate: string | null;
  sowReference: string | null;
  lostReason: string | null;
  createdAt: string;

  /** Every month the plan spans, `YYYY-MM`, in order. */
  months: string[];

  /**
   * The effective card rate per Labor Category on this plan's Rate Basis.
   *
   * Here so the inline add-line row can show and prefill the default rate BEFORE a line
   * exists — the per-line `listRate` cannot, because it is read off a line. Bill rates
   * only: no cost, so this is safe for [ACCT].
   */
  cardRates: Record<string, string>;

  indicativeRevenue: string;
  /**
   * RP-D16: the Contracted Fee over the plan's window — `contractedFee × months` on a
   * Monthly basis, the fee itself on a Total basis. What the margin of record and the
   * approval shortfall measure. Null with no fee. Optional: absent on versions frozen
   * before it existed.
   */
  contractedFeeOfRecord?: string | null;
  feeVariance: string | null;
  feeVariancePercent: string | null;
  discount: string;
  indicativeMarginPercent: string | null;
  contractedMarginPercent: string | null;

  /** The floor and band the badge is measured against, and where each came from. */
  marginFloorPercent: string;
  marginAmberBandPoints: string;
  marginFloorSource: string;
  marginAmberBandSource: string;
  /** The resolved basis's Card Margin — "57.2% vs 59.5% Enterprise card" (RP-7.1). */
  cardMarginPercent: string | null;

  /** Whole-plan estimate reconciliation, for the summary strip. */
  estimateVariancePercent: string | null;
  estimateVarianceHours: string | null;
  estimateBand: string;

  /** RP-6.2, as a count so the header can say "3 lines below cost" without magnitudes. */
  belowCostLineCount: number;

  /**
   * Whether this reader may edit the plan — they hold manageResourcePlan. Every status:
   * outside Draft the edit is recorded in Plan History (editMode in plan-access.ts).
   * Deleting is separate and stays Draft-only.
   */
  canEdit: boolean;
  canViewCost: boolean;
  /**
   * Whether this reader holds `manageResourcePlan` on this plan, INDEPENDENT of status.
   *
   * Distinct from `canEdit`, which also requires the plan to be a Draft. The status control
   * has to be available precisely when the plan is not editable — that is what it is for —
   * so it cannot hang off `canEdit`.
   *
   * Optional so a frozen `resource_plan_version` snapshot written before this field existed
   * still satisfies `isPlanVersionSnapshot`; the version shaper forces it false regardless.
   */
  canManageStatus?: boolean;
  /**
   * Whether this reader may set the Resourcing Status — holds `assignPrimary` ([RM]).
   * Independent of plan status. Optional for frozen snapshots; forced false on a version.
   */
  canSetResourcingStatus?: boolean;
  /**
   * Whether this reader may reassign the Owner — holds `manageResourcePlan` on this plan,
   * at ANY status. Optional for frozen snapshots; forced false on a version.
   */
  canChangeOwner?: boolean;
  /**
   * Whether this reader may change the Suggested Resource on a Plan Line — holds
   * `manageResourcePlan` on this plan, at ANY status. Unlike `canEdit`, this survives the
   * plan leaving Draft. Optional for frozen snapshots; forced false on a version.
   */
  canEditResources?: boolean;
  /**
   * RP-24 — whether this reader may split Plan Lines across Resources: holds
   * `managePlanLineShares` (ADMIN / RM) and the plan is not Locked. Optional; absent on a
   * frozen version.
   */
  canManageShares?: boolean;
  /** RP-24 — the Planning Constant cap on Shares per line. Absent on a frozen version. */
  maxPlanLineShares?: number;
  /**
   * Whether this reader may reject the plan — holds `rejectResourcePlan` AND the plan's
   * Resourcing status is In Review or Staffing, and it is not Lost / Closed / Superseded /
   * Locked. Optional for frozen snapshots; forced false on a version.
   */
  canReject?: boolean;
  /**
   * Whether this reader may lock the plan, or unlock it — holds `lockResourcePlan` (RM /
   * ADMIN, 2026-10-14) as well as `manageResourcePlan`. Optional; absent on frozen
   * snapshots and older payloads, which reads as "no".
   */
  canLock?: boolean;
  /**
   * Whether this reader may submit the plan to Resourcing — holds `manageResourcePlan`, the
   * Resourcing status is Not set or Rejected, and the plan is not Lost / Closed / Superseded /
   * Locked. True without a NOS Deal: the submit dialog says to link one. Absent on frozen
   * snapshots, which reads as "no".
   */
  canSubmitToResourcing?: boolean;

  // ── Versions after Won ("Commit version") ──
  /**
   * Whether this reader edits the plan as a draft against its live version: holds
   * `manageResourcePlan` and the status is Won / Handed Over / Staffed / Active / Closed.
   * Decides what a restore brings back (content only from Won on); whether the plan can be
   * edited at all is `canEdit`. Optional for frozen snapshots.
   */
  canEditAfterWon?: boolean;
  /**
   * "New version" is open: the reader can edit (any status but Locked) and there is
   * something to save — unsaved changes, or no version at all yet.
   */
  canCommitVersion?: boolean;
  /**
   * The grid holds changes no version has captured — stamped edits, or (for a plan last
   * edited before 2026-10-19) no live version and none at the plan's own number.
   */
  hasUnsavedChanges?: boolean;
  /** Whether this reader may restore a version into the plan (`canEdit || canEditAfterWon`). */
  canRestoreVersion?: boolean;
  /** ISO — when the first unsaved change after the live version was made, in any status; null when none. */
  uncommittedChangesAt?: string | null;

  /** Legal next statuses for this reader, from the transition map. */
  allowedTransitions: string[];

  // ── M4 governance (Epics 8, 17) ──
  /**
   * RP-17.1's "Draft (Awaiting Approval)" — set when an Approval Request is open.
   *
   * There is no eleventh plan status: the floor blocks the TRANSITION, not the plan, so a
   * plan awaiting approval is still `Draft` and still editable (that is the point — the
   * planner is meant to be able to fix the margin). This is what makes the header say so.
   */
  pendingApprovalId?: string | null;
  /** The latest Approval Request's status, so a rejection is visible without a second fetch. */
  approvalStatus?: string | null;
  /** How many frozen versions exist, so the Versions tab can carry a count (RP-8.1). */
  frozenVersionCount?: number;

  // ── M3 ──
  /** Σ of what was typed, before ramp. Equal to `totalHours` on an unramped plan (RP-4.3). */
  enteredHours?: string;
  /** Whether any month carries a factor below 1.0 — drives the "ramped" chip. */
  ramped?: boolean;
  /** The Planned Pod (Epic 2). Null until one is composed; a plan can be priced without one. */
  plannedPod?: PlannedPodView | null;

  workstreams: WorkstreamView[];
  lines: PlanLineView[];
  byMonth: PlanMonthView[];
  /**
   * The Estimated Hours Lines outline, depth-first (roots in `position` order, each
   * followed immediately by its own children). Reference only — this never drives
   * `lines`/`byMonth` above; `estimatedHours` and each Workstream's own `estimatedHours`
   * are the server-maintained rollup of this array (src/api/estimated-hours-lines.ts).
   * Safe for [ACCT]: no cost at any depth.
   */
  estimatedHoursLines: EstimatedHoursLineView[];

  /** Absent without `viewPlanCost`. */
  totalCost?: string;
  /** Absent without `viewPlanCost`. */
  totalNet?: string;
  /** Absent without `viewPlanCost`. */
  blendedCost?: string;
  /**
   * RP-7.5 — the margin of record with every hour at its Labor Category cost, i.e. before
   * any named Resource's Internal Rate. Null when no line is priced at an Internal Rate.
   * Absent without `viewResourceInternalRates` (ADMIN / FIN / RM).
   */
  laborCategoryBaselineMarginPercent?: string | null;
  /**
   * RP-22.2 — Staffed Margin: the margin of record with each Plan Line priced at whoever
   * holds the work on the Solution, month by month. Present only on a plan whose
   * Resourcing status is Staffed, for `viewPlanCost` + `viewResourceInternalRates` holders
   * (ADMIN / FIN / RM). Plan grain only.
   */
  staffedMarginPercent?: string | null;
  /** RP-22.2 — Plan Lines with hours in a month nobody on the Solution holds. */
  staffedUnstaffedLineCount?: number;
}

/**
 * The Planned Pod (Resource Plan Epic 2).
 *
 * Points are ABSENT from every level of this payload except as the `defaultPoints`
 * REFERENCE figure per Position, which RP-2.1 states plainly is "shown as a reference
 * figure only — they are not written anywhere". It is a string for exactly that reason
 * (non-negotiable 1, the hours-to-points firewall): a number invites arithmetic.
 */
export interface PlannedPodView {
  /** Template | Modified | Custom (RP-2.1 … RP-2.3). */
  shapeSource: string;
  /** The originating Shape. Null on Custom. */
  shapeId: string | null;
  /** The Shape's name as it was when chosen, snapshotted (RP-2.1). */
  shapeName: string | null;
  /**
   * RP-2.2: "The panel shows origin and deviations on one line" — e.g.
   * "Standard Trio, +1 SA, CS removed". Just the Shape name when nothing deviates.
   */
  originSummary: string;
  deviations: Array<{ kind: string; positionCode: string; text: string }>;
  /** RP-2.3: "closest: Quad, differs by 2 Positions". Computed, never stored. */
  nearestShape: {
    shapeId: string;
    shapeName: string;
    distance: number;
    summary: string;
  } | null;
  positions: PlannedPodPositionView[];
  /** RP-2.4: Workstream lines with hours that no Position covers. A flag, never a block. */
  uncoveredLineIds: string[];
}

export interface PlannedPodPositionView {
  id: string;
  positionCode: string;
  expectedDisciplineId: string;
  expectedDisciplineCode: string;
  expectedDisciplineName: string;
  /** Null where the Discipline has no priced Labor Category — the CS case (RP-11.2 BC). */
  laborCategoryId: string | null;
  laborCategoryName: string | null;
  deviationNote: string | null;
  sortOrder: number;

  /**
   * The tier's default points for this Position — a REFERENCE STRING, never a number and
   * never persisted (RP-2.1, non-negotiable 1). Null when the tier does not define one.
   */
  defaultPoints: string | null;

  /** RP-2.4: planned hours per Position, and per Position per month. */
  totalHours: string;
  byMonth: Array<{ month: string; hours: string }>;
  /** hours ÷ hoursPerFteMonth. A reference figure (RP-2.4). */
  fte: string;
  /** RP-2.4: "Positions with no linked hours flag 'Position with no planned work'." */
  hasPlannedWork: boolean;
  linkedLineIds: string[];
}

/** One Workstream Template version (RP-13.1). */
export interface WorkstreamTemplateView {
  id: string;
  type: string;
  version: number;
  description: string | null;
  active: boolean;
  sizeSHours: string;
  sizeMHours: string;
  sizeLHours: string;
  sizeXlHours: string;
  lines: Array<{
    laborCategoryId: string;
    laborCategoryName: string;
    /** Null where the category maps to no Discipline — not Pod-eligible (RP-11.2). */
    disciplineCode: string | null;
    mixPercent: string;
    /** True where the category has since been deactivated (RP-11.1). A flag, not a block. */
    laborCategoryInactive: boolean;
  }>;
  /** RP-3.4: "the type picker shows the template's mix summary inline". */
  mixSummary: string;
  /** How many Workstreams reference this version — why the FK is `restrict`. */
  workstreamCount: number;
}

/**
 * A solve proposal (Epic 5). Returned by `/solve`, never written by it.
 *
 * NO COST FIELD, at any grain. RP-5.3: "[ACCT] sees the resulting price and Blended Rate.
 * Cost inputs are not returned to the client." The margin solve consumes cost to answer
 * at all, but nothing derived from it beyond a percentage crosses this boundary.
 */
export interface PlanSolveProposalView {
  kind: string;
  /** Set when the solve cannot be satisfied. The caller must refuse to apply. */
  blocked: string | null;
  /** RP-5.1's shortfall in dollars, when locked lines already exceed the target. */
  shortfall: string | null;

  totalHours: string | null;
  totalRevenue: string | null;
  blendedRate: string | null;
  changes: Array<{
    lineId: string;
    laborCategoryName: string;
    clientLaborCategory: string | null;
    fromHours: string;
    toHours: string;
    deltaHours: string;
    locked: boolean;
  }>;

  // ── targetMargin only (RP-5.3) ──
  /** The revenue, or Fixed-Price fee, that hits the target. */
  requiredRevenue?: string;
  /** requiredRevenue ÷ current hours. What one hour has to average. */
  impliedBlendedRate?: string | null;
  /** Signed: positive means raise the price. */
  uplift?: string;
  /** The margin the plan makes at its current applied rates. */
  currentMarginPercent?: string | null;
}

/**
 * The Solution a plan prices, as the plan header shows it (RP-15.1).
 *
 * Deliberately a SNAPSHOT for display, not an editing surface: every field here is owned
 * by Solution Detail, and the header links there rather than duplicating its controls.
 * The Fit Score is the Solution's own snapshotted score — the same figure the Solutions
 * Dashboard shows — not the per-candidate score in the Resource picker.
 */
export interface SolutionContextView {
  solutionId: string;
  solutionName: string;
  /** From the Account; a Solution has no Industry of its own. */
  industry: string | null;
  intensity: number;
  /** The EFFECTIVE level, after the Solution-over-Account precedence. */
  expectationLevel: number;
  /** True when the effective level came from the Account rather than a Solution override. */
  expectationInherited: boolean;
  tierName: string | null;
  tierInherited: boolean;
  fit: { score: string; tier: "Strong" | "Moderate" | "At Risk" } | null;
  /** Grouped in the UI by `groupName` — Offering / Platform / Domain / ICP. */
  constraints: Array<{ id: string; name: string; groupName: string; inherited: boolean }>;
}

/**
 * One candidate's Fit Score for the plan's Solution (RP-18.2).
 *
 * The Solution-level Fit Score, asked of one Resource instead of a staffed Pod: the same
 * Constraint Ratings, the same expectation decay against the same Expectation Level, the
 * same per-tier weights and the same Strong / Moderate / At Risk bands. Availability is
 * absent — it is capacity coverage across a whole Pod and has no per-candidate meaning —
 * so the weights are renormalized over the dimensions that do. See `computeCandidateFits`
 * in `src/api/fit-score.ts`.
 *
 * ALWAYS `null` for an actor without `viewResourceDetail` at `any` scope. Every figure
 * below is derived from RM-confirmed ratings of a Resource, so it is shown only to a role
 * that may already read those ratings on Resource Detail — since 2026-10-08 that is every
 * role that reaches the picker, [ACCT] included (system-design §5 footnote 9).
 */
export interface ResourcePickerFitView {
  /** 0–100, two decimals. */
  score: string;
  tier: "Strong" | "Moderate" | "At Risk";
  /**
   * Only one dimension had inputs, so this score is NOT comparable with a two-dimension
   * one and the UI must say so. A Labor Category's Discipline mapping is optional, so two
   * Plan Lines in one plan can genuinely be scored differently.
   */
  partial: boolean;
  /** Absent when the Solution carries no Constraints. */
  constraintMatch: string | null;
  /** Absent when the Plan Line's Labor Category maps to no Discipline. */
  expectation: string | null;
  /** The Discipline the score was asked against — the Labor Category's. */
  disciplineCode: string | null;
  /** The confirmed Discipline Rating used, or null when there is none (scored as 1). */
  disciplineRating: number | null;
  expectationLevel: number;
  ratedConstraints: number;
  totalConstraints: number;
}

/**
 * One Plan Line's suggested Resource, scored for the plan's Solution against that line's
 * Labor Category Discipline — the grid's Who column. Same gate as the picker's score:
 * absent entirely for an actor without `viewResourceDetail`.
 */
export interface PlanLineFitView {
  lineId: string;
  fit: ResourcePickerFitView;
}

/**
 * The Suggested Resource picker payload (RP-18.2).
 *
 * Name, Discipline, prior Account history, and — for an actor who may already see
 * Resource Detail — the candidate's Fit Score for this Solution. No capacity, no raw
 * ratings, no cost, no Resource Detail link. This is a new, narrow surface — never the
 * directory with a flag.
 */
export interface ResourcePickerEntryView {
  id: string;
  name: string;
  disciplines: Array<{ code: string; name: string }>;
  priorAccounts: Array<{ id: string; name: string }>;
  /** `null` when the actor may not see it, or when nothing could be scored. */
  fit: ResourcePickerFitView | null;
  /**
   * The candidate's in-force Account reservation (docs/specs/resource-reservation.md), or null.
   * Deliberately narrow — no note, no who/when — because [ACCT] reaches this picker. The
   * Account name is no wider than `priorAccounts`, which this surface already carries.
   */
  reservation: ResourcePickerReservationView | null;

  // ── RP-33.1 / 33.2 (2026-10-17). Every field below is null / empty for an actor without
  // `viewResourceDetail: any` (the same gate as `fit`). Since #189 every role that reaches the
  // picker holds it — RP-18.2's "no capacity for [ACCT]" was overtaken by that decision.
  /** The Resource's CONFIRMED rating for the line's Discipline; null when unrated or none mapped. */
  rating: number | null;
  /** Under "Show all": how the Resource sits against the line's Discipline and rating range. */
  mismatch: ResourcePickerMismatchView | null;
  /** Free NOS hours over the line's months (src/lib/resource-availability.ts). */
  availability: ResourceAvailabilityView | null;
  /** Other open plans naming this Resource (Suggested, or a Candidate) — never subtracted. */
  otherPlans: ResourcePickerOtherPlanView[];
  otherPlanCount: number;
  /** A deterministic one-line reason, top 3 of the first page only (RP-33.4 as shipped). */
  reason: string | null;
}

export interface ResourcePickerMismatchView {
  /** True when the Resource does not hold the line's Labor Category Discipline. */
  discipline: boolean;
  rating: "below" | "above" | "unrated" | null;
}

export interface ResourceAvailabilityView {
  /** Total plan: hours summed over the measured months. Monthly plan: a monthly average. */
  basis: "Total" | "Monthly";
  /** NOS capacity − NOS guidance hours; null when NOS has no figure for these months. */
  freeHours: number | null;
  capacityHours: number | null;
  guidanceHours: number | null;
  months: Array<{ month: string; capacityHours: number; guidanceHours: number; free: number }>;
  /** Measured months NOS has no figure for yet (`YYYY-MM`). */
  missingMonths: string[];
}

export interface ResourcePickerOtherPlanView {
  planId: string;
  name: string;
  status: string;
  kind: "Suggested" | "Candidate";
}

/** RP-33 — one cursor page of the Resource picker (cursor shape per .claude/rules/api.md). */
export interface ResourcePickerPageView {
  data: ResourcePickerEntryView[];
  nextCursor: string | null;
  prevCursor: null;
  hasNext: boolean;
  hasPrev: false;
  total: number;
  context: ResourcePickerContextView;
}

export interface ResourcePickerContextView {
  /** The months hours free is measured over, `YYYY-MM` (from now on; three for Monthly). */
  window: string[];
  /** Total plans sum hours free over `window`; Monthly plans average it. */
  basis: "Total" | "Monthly";
  /** True when NOS could not be read, so nobody has an hours figure. */
  guidanceUnavailable: boolean;
  /** The line's Labor Category Discipline, when it maps to one. */
  disciplineCode: string | null;
  ratingRange: { min: number; max: number } | null;
  /** True when the default filter is narrowing the list (a Discipline exists, Show all off). */
  filtered: boolean;
  /** True on an R1–R4 line — the only lines that show a Fit Score (RP-33 Decision 1). */
  primary: boolean;
}

// ── RP-32.2 / 32.3 — Plan Line Candidates ────────────────────────────────────

export const addPlanLineCandidateSchema = z.object({
  resourceId: z.string().min(1),
});

/** One active Candidate on a Plan Line. Figures follow the picker's gate. */
export interface PlanLineCandidateView {
  id: string;
  resourceId: string;
  name: string;
  disciplines: Array<{ code: string; name: string }>;
  rating: number | null;
  availability: ResourceAvailabilityView | null;
  otherPlans: ResourcePickerOtherPlanView[];
  otherPlanCount: number;
  /** Only on an R1–R4 line, as in the picker. */
  fit: ResourcePickerFitView | null;
  addedByName: string | null;
  addedAt: string;
}

/** A Candidate that left the shortlist — kept as history (RP-32.3). */
export interface PlanLineCandidateHistoryView {
  id: string;
  name: string;
  outcome: "Removed" | "Promoted" | "ClearedOnPromote" | "ClearedOnStaffed" | "LineRemoved";
  clearedAt: string;
  clearedByName: string | null;
}

export interface PlanLineCandidatesView {
  lineId: string;
  /** The months hours free is measured over, `YYYY-MM` (from now on; three for Monthly). */
  window: string[];
  basis: "Total" | "Monthly";
  candidates: PlanLineCandidateView[];
  history: PlanLineCandidateHistoryView[];
  /** At most this many active Candidates per line. */
  cap: number;
  /** Whether the actor may add, remove or promote here right now (capability + status). */
  canManage: boolean;
  /** Why not, when the capability is held but the plan's state refuses. */
  lockedReason: string | null;
}

// ── RP-32.4 — Plan comments (line notes, plan-level comments; RP-34 Concern replies reuse the shape) ──

export const addPlanCommentSchema = z.object({
  /**
   * The line the note is on. Absent = a comment on the whole plan, posted from the plan's
   * Activity tab. RP-34 adds `concernId` as a further scope.
   */
  planLineId: z.string().min(1).optional(),
  body: z
    .string()
    .trim()
    .min(1, { message: "Write something first." })
    .max(2000, { message: "Keep a note to 2,000 characters." }),
});

export const listPlanCommentsQuerySchema = z.object({
  planLineId: z.string().min(1).optional(),
});

export interface PlanCommentView {
  id: string;
  /**
   * Null once the line itself is gone — the note stays, under `lineLabel`. Null with a null
   * `lineLabel` too: a comment on the whole plan.
   */
  planLineId: string | null;
  lineLabel: string | null;
  /** Null when the note was removed ("Note removed"). */
  body: string | null;
  author: { id: string; name: string } | null;
  createdAt: string;
  deletedAt: string | null;
  deletedByName: string | null;
  /** The author may remove their own note; nobody else may. */
  canDelete: boolean;
}

export interface PlanCommentsView {
  data: PlanCommentView[];
  /** Everyone who can see the plan can write (no hidden notes in v1). */
  canWrite: boolean;
}

export const resourcePickerQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  lineId: z.string().min(1).optional(),
  showAll: z.enum(["true", "false"]).optional(),
  cursor: z.string().min(1).max(500).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});

/**
 * A Resource's in-force Account reservation as seen from one plan (docs/specs/resource-reservation.md).
 * Shared by the Suggested Resource picker and the grid's load bars.
 */
const planResourceReservationSchema = z.object({
  /** True when reserved for this plan's own Account — the picker ranks these first. */
  forThisAccount: z.boolean(),
  accountName: z.string(),
  /** Last day of the reservation, `YYYY-MM-DD`, inclusive. null = open-ended. */
  until: z.string().nullable(),
});

export type ResourcePickerReservationView = z.infer<typeof planResourceReservationSchema>;

/** One row of Plan History (RP-9.2), read from `audit_log`. */
export interface PlanHistoryEntryView {
  id: string;
  eventType: string;
  entityType: string;
  entityId: string;
  beforeState: unknown;
  afterState: unknown;
  actorUserId: string | null;
  actorName: string | null;
  reason: string | null;
  eventAt: string;
}

// ═════════════════════════════════════════════════════════════════════════════
// Resource Plan M4 — Governance (Epics 8, 17, 10)
// ═════════════════════════════════════════════════════════════════════════════

/*
 * The version-kind and approval-status vocabularies are NOT re-declared here.
 *
 * `PLAN_VERSION_KINDS` and its guard live in src/lib/plan-version.ts, beside the freeze
 * logic that produces them; the approval statuses are the `plan_approval_status` enum and
 * are validated by `decidePlanApprovalSchema`'s own `z.enum`. A second copy in this file
 * would be a second authority to keep in step, which is what the M1 rounding lesson and
 * the `WORKSTREAM_TYPES` duplication both already cost.
 */

// ── Epic 17 — Approve a plan (RP-17.1 … RP-17.3) ──────────────────────────────

/*
 * There is no `requestPlanApprovalSchema`, and there is no "request approval" endpoint.
 *
 * RP-17.1's request is raised BY the Draft → Proposed transition, not by a call of its own:
 * the planner cannot know whether approval is needed, because the gate decides that from
 * cost they are not shown. So the planner's note rides on `planTransitionSchema` as
 * `approvalNote` and is ignored when the gate does not fire — see that schema above.
 */

/**
 * RP-17.2: "approve or reject with a reason, so that the decision is recorded."
 *
 * A rejection's reason is REQUIRED — it is what the planner acts on, and RP-17.2 makes it
 * "visible to the planner" explicitly. An approval's is optional, because "approved" is a
 * complete answer. Enforced here and by `plan_approval_rejection_needs_reason` in SQL.
 */
export const decidePlanApprovalSchema = z
  .object({
    decision: z.enum(["Approved", "Rejected"]),
    reason: z.string().trim().max(1000).nullable().optional(),
  })
  .refine(
    (v) => v.decision !== "Rejected" || (v.reason !== null && (v.reason ?? "").trim() !== ""),
    { message: "a rejection needs a reason the planner can act on", path: ["reason"] },
  );

/**
 * One Plan Line priced below its Labor Category's cost, as frozen onto the request.
 *
 * 🔒 COST-DERIVED, every field of it. This shape only ever appears inside
 * `PlanApprovalDetailView`, which is gated on `viewPlanCost` — RP-6.2 is explicit that
 * "the MAGNITUDE of the below-cost delta is not shown to [ACCT]", and this is the
 * magnitude. `[ACCT]` gets the count, on the plan, and nothing else.
 */
export interface PlanApprovalBelowCostLine {
  planLineId: string;
  laborCategoryName: string;
  /** The applied bill rate. */
  billRate: string;
  /**
   * The line's cost rate: `labor_category.avg_internal_cost`, or its Suggested Resource's
   * Internal Rate (hour-weighted) when `internalRateSource` says so. NULL on a Resource-priced
   * line for a reader without `viewResourceInternalRates` — a named Resource's cost.
   */
  costRate: string | null;
  /** costRate − billRate, per hour. Positive: how far underwater. NULL exactly when costRate is. */
  deltaPerHour: string | null;
  /**
   * Where the cost came from, frozen at request time (2026-10-14). OPTIONAL: a request
   * written before this existed has no key and was priced at its Labor Category.
   */
  internalRateSource?: "labor_category" | "resource" | "mixed";
  totalHours: string;
  /**
   * Where the applied rate came from, frozen at request time. OPTIONAL: a request written
   * before this field existed has no key, and the exhibit then shows no source.
   */
  rateSource?: string;
  listRate?: string | null;
}

/**
 * What RP-17.3 shows the planner: "pending / approved / rejected with actor, timestamp,
 * and reason."
 *
 * Rides on `viewResourcePlan`. Deliberately carries NO margin figure, NO shortfall and NO
 * below-cost list — those are RP-17.1's approver exhibit and live in the detail view.
 */
export interface PlanApprovalSummaryView {
  id: string;
  resourcePlanId: string;
  versionNumber: number;
  /** Pending | Approved | Rejected. */
  status: string;
  /**
   * Propose — raised by Propose on a Draft; approving sends the plan out. Edit — raised by
   * a change to a plan that was already sent; approving only records the decision.
   */
  origin: "Propose" | "Edit";
  requestedAt: string;
  requestedByUserId: string | null;
  requestedByName: string | null;
  decidedAt: string | null;
  decidedByUserId: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  /** The `Approved` version this decision froze, when it approved (RP-17.2). */
  approvedVersionId: string | null;
}

/**
 * RP-17.1's approver exhibit: "margin %, floor, shortfall in dollars, the lines below
 * cost, and the planner's note", plus RP-17.2's "the Planned Pod, Shape Source … and
 * suggested Intensity per month, so the approver sees what staffing is about to inherit".
 *
 * 🔒 Gated on `viewPlanCost`, not on `approveResourcePlan` — the shortfall and the
 * below-cost deltas are cost-derived, and `viewPlanCost` is the one capability that
 * governs cost-derived figures (non-negotiable 3). Reading "visible to approvers only" as
 * `approveResourcePlan` would have hidden the shortfall from `[RM]`, who authors plans and
 * sees cost everywhere else — two subtly different cost boundaries instead of one. See the
 * header of src/db/schema/plan-approvals.ts.
 */
export interface PlanApprovalDetailView extends PlanApprovalSummaryView {
  planName: string;
  solutionName: string;
  clientAccountName: string;
  rateBasisLabel: string;
  requestedNote: string | null;

  /** Which of RP-17.1's two conditions fired. Both are recorded, not just the first. */
  triggerBelowFloor: boolean;
  triggerBelowCostLineCount: number;

  marginOfRecordPercent: string | null;
  marginFloorPercent: string;
  /** Revenue short of the floor, in dollars. Null when the trigger was below-cost alone. */
  shortfallAmount: string | null;
  /** RP-17.1: shown alongside the shortfall "whether the problem is the price or the scope". */
  feeVariance: string | null;
  belowCostLines: PlanApprovalBelowCostLine[];

  /** RP-17.2's staffing context. Hours and Positions only — the Pod view carries no cost. */
  plannedPod: PlannedPodView | null;
  /**
   * RP-17.2's "suggested Intensity per month".
   *
   * 🔥 A DISPLAY STRING, never a number (non-negotiable 1, the hours-to-points firewall).
   * §4.5: "treat any derived points value as a display string, never a persisted number."
   * Nothing writes these; the `[RM]` confirms an Intensity when the plan is handed over in
   * M5, and this is the reference figure an approver reads beforehand.
   */
  suggestedIntensityByMonth: Array<{
    month: string;
    hours: string;
    suggestedIntensity: string;
    /** Solo / Pair / Trio / Quad, from the §5 Intensity → Pod size ladder. */
    podComposition: string;
  }>;

  /** Whether this reader may decide it (`approveResourcePlan`), or is only looking. */
  canDecide: boolean;
}

/** One row of the approver's queue. Same cost gate as the detail view. */
export interface PlanApprovalQueueEntryView {
  id: string;
  resourcePlanId: string;
  planName: string;
  solutionName: string;
  clientAccountName: string;
  versionNumber: number;
  requestedAt: string;
  requestedByName: string | null;
  requestedNote: string | null;
  marginOfRecordPercent: string | null;
  marginFloorPercent: string;
  shortfallAmount: string | null;
  triggerBelowFloor: boolean;
  triggerBelowCostLineCount: number;
  /** What raised it — see PlanApprovalSummaryView.origin. */
  origin: "Propose" | "Edit";
  /** Days the request has been open — the figure that makes a queue actionable. */
  ageDays: number;
}

// ── Epic 8 — Version and compare (RP-8.1 … RP-8.4) ────────────────────────────

/**
 * RP-8.2: "Clone copies Workstreams, Plan Lines, hours, Planned Pod, and terms into a new
 * Draft plan or new version — planner chooses."
 *
 * `target: "plan"` makes a new plan; `target: "version"` restores a frozen version's
 * content into THIS plan as a new Draft version. The second REPLACES the live grid, which
 * is why it takes an explicit `versionId` rather than defaulting to the latest.
 */
export const clonePlanSchema = z.discriminatedUnion("target", [
  z.object({
    target: z.literal("plan"),
    name: name.optional(),
    kind: z.enum(PLAN_KINDS).optional(),
    /** RP-8.2: "Clone can shift the start month; hours move with it." */
    startMonth: month.optional(),
    /** RP-8.4's "reference field (SOW# / CO#)". Only meaningful with kind ChangeOrder. */
    changeOrderReference: z.string().trim().max(120).nullable().optional(),
    /** Copy from a frozen version rather than the live plan — "the last agreed shape". */
    fromVersionId: z.string().min(1).nullable().optional(),
  }),
  z.object({
    target: z.literal("version"),
    versionId: z.string().min(1),
  }),
]);

/** "Commit version" on a plan that is Won or later. An empty label means none. */
export const commitPlanVersionSchema = z.object({
  label: z
    .string()
    .trim()
    .max(120, "A version label can be at most 120 characters.")
    .nullable()
    .optional()
    .transform((value) => (value === undefined || value === null || value === "" ? null : value)),
});

/**
 * A comparable side: the live plan, or one frozen version.
 *
 * `live:<planId>` and `version:<versionId>` — one string, so a compare is a GET with two
 * query parameters rather than a POST for a read. Both sides are authorised
 * independently, because "compare two plans" can span two Accounts.
 */
export const planCompareRefSchema = z.string().regex(/^(live|version):[A-Za-z0-9_-]{1,64}$/, {
  message: 'must be "live:<planId>" or "version:<versionId>"',
});

export const planCompareQuerySchema = z.object({
  left: planCompareRefSchema,
  right: planCompareRefSchema,
});

/** One frozen version, as the versions panel lists it. Hours, revenue, percentages — no cost. */
export interface PlanVersionSummaryView {
  id: string;
  resourcePlanId: string;
  versionNumber: number;
  /** Sent | Approved | Won | Committed. */
  kind: string;
  label: string;
  /** The planner's own name for a Committed version; null when none was given. */
  commitLabel: string | null;
  planStatusAtFreeze: string;
  frozenAt: string;
  frozenByUserId: string | null;
  frozenByName: string | null;

  totalHours: string;
  enteredHours: string;
  indicativeRevenue: string;
  contractedFee: string | null;
  blendedRate: string | null;
  marginOfRecordPercent: string | null;
  marginOfRecordKind: string;
  marginStatus: string | null;
  /** The floor THIS version was measured against — RP-14.1: frozen versions keep it. */
  marginFloorPercent: string;
  lineCount: number;
  shapeSource: string | null;
  shapeName: string | null;
}

/** One version as the versions list shows it to this reader. */
export interface PlanVersionListItemView extends PlanVersionSummaryView {
  /** The version the live plan currently matches — the one last saved, frozen or restored. */
  isLive: boolean;
  /** Whether this reader may delete it: they saved it, it is not live, the plan is not Locked. */
  canDelete: boolean;
}

/**
 * One frozen version, opened.
 *
 * `plan` is the frozen detail view, shaped for the reader — cost keys present only with
 * `viewPlanCost`, exactly as on a live plan. `canEdit` is always false and
 * `allowedTransitions` always empty: a version is history.
 */
export interface PlanVersionDetailView extends PlanVersionSummaryView {
  plan: ResourcePlanDetailView;
  /** The card rates in force at the freeze (RP-8.1's "applied rates", RP-12.5). */
  cardRates: Record<string, string>;
}

// ── Epic 10 — Export (RP-10.1, RP-10.2) ───────────────────────────────────────

/**
 * The export request, parsed from the query string.
 *
 * `variant` decides what the document contains — the exclusion list lives in
 * src/lib/plan-export/model.ts, not here. `internal` is gated on `viewPlanCost` at the
 * route (RP-10.2: "Available to RM/FIN/ADMIN only"), which is the same set.
 */
export const planExportQuerySchema = z.object({
  variant: z.enum(["client", "clientRatesFree", "internal"]).default("client"),
  format: z.enum(["xlsx", "pdf"]).default("xlsx"),
  /** Export a frozen version instead of the live plan, so what was sent is reproducible. */
  versionId: z.string().min(1).nullable().optional(),
});

// ── Shared Plan Overview (the read-only view a plan is shared as) ─────────────
//
// A SEPARATE, NARROWER type — deliberately not a `Partial<ResourcePlanDetailView>` and
// deliberately not built by deleting keys.
//
// `toSharedPlanView()` in src/lib/plan-share-view.ts constructs these interfaces field by
// field from an ALLOW-LIST. That is the whole safety property: a field added to
// `ResourcePlanDetailView` later cannot reach the shared view by default — someone has to
// come here and add it on purpose. A delete-list would have the opposite failure mode,
// which is the one that leaks.
//
// Nothing cost-derived appears at any grain, and — unlike the working view, where margin
// percentages are safe for [ACCT] and do travel — NO MARGIN travels here either, at any
// grain. The shared view is bill-side only: hours, rates, revenue, shape. It is the same
// whoever opens it, so there is no reader-dependent shaping to get wrong and no version of
// this payload that carries cost.

/**
 * One cell of the shared grid. `hours` is EFFECTIVE — entered × ramp — so a line's cells
 * sum to its `totalHours` and a month's cells to that month's `totalHours`.
 */
export interface SharedPlanLineMonthView {
  month: string;
  hours: string;
}

export interface SharedPlanLineView {
  id: string;
  workstreamId: string | null;
  laborCategoryName: string;
  disciplineCode: string | null;
  clientLaborCategory: string | null;
  position: string | null;
  billRate: string;
  sortOrder: string;
  totalHours: string;
  totalRevenue: string;
  /** The line's hours by month, inside the plan's window. Months with no cell are absent. */
  months: SharedPlanLineMonthView[];
  /**
   * Open, Proposed or Confirmed — the status only. WHO is named never travels: the
   * Resource's id and name stay out of the shared view.
   */
  lineStatus: PlanLineStatus;
}

export interface SharedPlanWorkstreamView {
  id: string | null;
  name: string;
  type: string | null;
  description: string | null;
  sortOrder: string;
  startMonth: string | null;
  endMonth: string | null;
  totalHours: string;
  totalRevenue: string;
  blendedRate: string | null;
  lineCount: number;
}

export interface SharedPlanMonthView {
  month: string;
  totalHours: string;
  totalRevenue: string;
  /** hours ÷ hoursPerFteMonth. A reference figure, never persisted (§4.5). */
  fte: string;
}

export interface SharedPlanPodPositionView {
  id: string;
  positionCode: string;
  expectedDisciplineCode: string;
  expectedDisciplineName: string;
  laborCategoryName: string | null;
  deviationNote: string | null;
  sortOrder: number;
  totalHours: string;
  fte: string;
  hasPlannedWork: boolean;
}

export interface SharedPlanPodView {
  shapeSource: string;
  shapeName: string | null;
  originSummary: string;
  positions: SharedPlanPodPositionView[];
}

export interface SharedPlanView {
  id: string;
  name: string;
  kind: string;
  status: string;
  solutionId: string;
  solutionName: string;
  clientAccountId: string;
  clientAccountName: string;
  ownerName: string | null;
  updatedAt: string;
  /** Which version the plan is at, so a shared figure can be traced to a build. */
  versionNumber?: number;

  // ── Shape ──
  startMonth: string;
  endMonth: string | null;
  /** Every month the plan spans, `YYYY-MM`, in order. */
  months: string[];
  entryMode: string;
  /** Whether any month carries a ramp factor below 1.0. */
  ramped?: boolean;

  // ── Commercial, bill-side only ──
  pricingModel: string;
  budgetCadence: string;
  rateBasisLabel: string;
  engagementStandardTierName: string | null;
  contractedFee: string | null;
  contractedFeeBasis: string | null;
  sowReference: string | null;
  wonDate: string | null;
  discountPercent: string | null;
  discountAmount: string | null;
  feeVariance: string | null;
  feeVariancePercent: string | null;

  // ── Totals ──
  totalHours: string;
  totalRevenue: string;
  indicativeRevenue: string;
  blendedRate: string | null;
  lineCount: number;
  estimatedHours: string | null;

  // ── Detail ──
  pod: SharedPlanPodView | null;
  workstreams: SharedPlanWorkstreamView[];
  lines: SharedPlanLineView[];
  byMonth: SharedPlanMonthView[];
}

// ── Plan Guidance Load (GET /api/plans/:planId/guidance-load) ─────────────────
//
// Read-only: NOS guidance + capacity hours, per month, for the Resources suggested
// on a plan's lines — narrowed to the Resources the actor may view under
// `viewResourceDetail`. This is a RESPONSE shape, but unlike the interfaces above
// it is declared as a Zod schema per the task contract, so `PlanGuidanceLoadView`
// is the inferred type rather than a hand-written interface.

const planGuidanceLoadMonthSchema = z.object({
  /** `YYYY-MM`, matching `PlanLineMonthView.month`. */
  month,
  guidanceHours: z.number().nullable(),
  capacityHours: z.number().nullable(),
  /**
   * Hours reserved for ANOTHER Account this month (capacity minus guidance) — they count as
   * load on this plan. 0 when the Resource is unreserved, reserved for this plan's own
   * Account, or the reservation has ended by this month; null when guidance/capacity is unknown.
   */
  reservedHours: z.number().nullable(),
});

const planGuidanceLoadResourceSchema = z.object({
  resourceId: z.string(),
  months: z.array(planGuidanceLoadMonthSchema),
  /** The Resource's in-force Account reservation, or null. */
  reservation: planResourceReservationSchema.nullable(),
});

export const planGuidanceLoadViewSchema = z.object({
  resources: z.array(planGuidanceLoadResourceSchema),
  /** Distinct suggested Resource ids on this plan the actor may view. */
  viewableResourceIds: z.array(z.string()),
  /**
   * False once the plan has been handed over — its hours are already in NOS, so
   * showing the plan's own demand on top of NOS guidance would double-count it.
   */
  includesPlanHours: z.boolean(),
  /** True when the NOS gateway call failed — callers keep rendering, greyed out. */
  guidanceUnavailable: z.boolean(),
});

export type PlanGuidanceLoadView = z.infer<typeof planGuidanceLoadViewSchema>;
